require('./libs/aws-sdk-browser.js');
var AWS = window.AWS;
var tool = require('./tool');
var dataService = require('./dataService');
var noop = tool.noop;
var isFunction = tool.isFunction;
var replaceAWSPath = tool.replaceAWSPath;
var hasResourceCallback = tool.hasResourceCallback;
var logger = require('./logger');

// 存储当前上传任务信息
var currentUploadTasks = {};

/**
 * 清理指定的上传任务信息
 * @param {string} uploadId - 上传ID
 */
function cleanupTask(uploadId) {
    if (uploadId && currentUploadTasks[uploadId]) {
        logger.log('[umu-upload aws] cleanupTask', uploadId);
        delete currentUploadTasks[uploadId];
    }
}

/**
 * 取消上传任务
 * @param {string} uploadId - 上传ID，由upload方法返回
 * @returns {boolean} 是否成功取消任务
 */
function cancelUpload(uploadId) {
    if (!uploadId) {
        logger.errorAndLog('[umu-upload aws] cancelUpload uploadId invalid', uploadId);
        return false;
    }

    var taskInfo = currentUploadTasks[uploadId];
    if (!taskInfo) {
        logger.errorAndLog('[umu-upload aws] cancelUpload task not found', uploadId);
        return false;
    }

    try {
        // 使用AWS SDK的取消上传功能
        if (taskInfo.awsRequest) {
            taskInfo.awsRequest.abort();
            logger.log('[umu-upload aws] cancelUpload task aborted', uploadId);
        }

        // 清理任务信息
        cleanupTask(uploadId);

        // 触发取消回调
        if (taskInfo.cancelCallback && isFunction(taskInfo.cancelCallback)) {
            taskInfo.cancelCallback();
        }

        return true;
    } catch (error) {
        logger.errorAndLog('[umu-upload aws] cancelUpload error', error);
        return false;
    }
}

function initSDK(region) {
    return dataService.assumerole(region).then(function (data) {
        var authConfig = {
            region: region,
            accessKeyId: data.Credentials.AccessKeyId,
            secretAccessKey: data.Credentials.SecretAccessKey,
            sessionToken: data.Credentials.SessionToken,
        };
        AWS.config.update(authConfig);
    });
}

function getUploadObj(params) {
    var ext = params.ext;
    var id = params.id;
    return dataService.getUploadObj(
        Object.assign(
            {
                id: id,
                media_type: '',
                ext: ext,
            },
            params
        )
    );
}

function uploadCallback(params) {
    var ext = params.ext;
    return dataService.uploadCallback(
        Object.assign(
            {
                media_type: '',
                sp: 2,
                ext: ext,
            },
            params
        )
    );
}

/**
 * @params Object options
 * @params string options.file
 * @params number options.key
 * @params number options.bucket
 * @params number options.resourceId
 * @params function options.error
 * @params string options.uploadId - 上传ID
 */
function awsUpload(options, customEndpoint) {
    return new Promise(function (resolve, reject) {
        var s3Params = {
            params: { Bucket: options.bucket || '' },
        };
        if (customEndpoint) {
            s3Params.endpoint = customEndpoint;
        }
        var s3 = new AWS.S3(s3Params);
        var file = options.file;
        var mimeType = tool.getMimeType(file);
        var params = {
            Key: options.key,
            Body: file,
            ContentType: mimeType,
        };
        var request = s3.upload(params, function (err, data) {
            if (err) {
                // 检查是否是用户取消的错误
                if (err.code === 'RequestAbortedError') {
                    logger.log('[umu-upload aws] upload canceled by user');
                    return; // 用户取消，不执行reject
                }
                reject(err);
                logger.errorAndLog('[umu-upload aws] upload error:', err, data);
            } else {
                // {
                //     Bucket: "umu.pd"
                //     Key: "4.jpg"
                //     Location: "https://s3.ap-northeast-1.amazonaws.com/umu.pd/4.jpg"
                //     key: "4.jpg"
                // }
                resolve(data);
                request.removeAllListeners();
                logger.log('[umu-upload aws] upload success:', data); // successful response
            }
        });

        // 存储request到任务信息中以便取消
        if (options.uploadId && currentUploadTasks[options.uploadId]) {
            currentUploadTasks[options.uploadId].awsRequest = request;
        }

        var onProgress = function (e) {
            // 检查任务是否被取消
            if (options.uploadId && !currentUploadTasks[options.uploadId]) {
                logger.log('[umu-upload aws] task canceled, stop progress callback');
                return;
            }

            // {
            //     key: "1--PxEo2i3swE2lhu81vXcIw.jpeg"
            //     lengthComputable: true
            //     loaded: 163840
            //     part: 1
            //     path: []
            //     total: 958074
            //     type: "progress"
            // }
            const percent = parseFloat((e.loaded / e.total || 0).toFixed(2));
            e.percent = percent;
            options.progress(e);
            // 暂时关闭进度日志的上传，因为上传日志量太大
            // logger.log('[umu-upload aws] upload progress', e.loaded + ' of ' + e.total + ' bytes');
        };
        request.on('httpUploadProgress', onProgress);
    });
}

/**
 * @description 大小检查
 */
function isOverMaxSize(file, maxSize, callback) {
    var status = false;
    callback = callback || noop;
    var maxFileSize = tool.parseSize(maxSize);
    if (maxFileSize > 0 && file.size > maxFileSize) {
        // options.onOverMaxSize.apply(null, arguments);
        callback();
        status = true;
    }
    return status;
}

function upload(file, options) {
    options = Object.assign(
        {
            beforeUpload: noop,
            success: noop,
            error: noop,
            progress: noop,
        },
        options
    );

    // 生成唯一的上传ID
    var uploadId = file.uniqueId || 'aws_upload_' + Date.now() + '_' + Math.random().toString(36).slice(2, 11);

    // 初始化任务信息
    currentUploadTasks[uploadId] = {
        file: file,
        options: options,
        startTime: Date.now(),
        cancelCallback: options.onCancel,
    };

    var ext = file.name.split(/\./g).pop();

    //beforeUpload 之前文件校验
    if (isOverMaxSize(file, options.maxFileSize, options.onOverMaxSize)) {
        cleanupTask(uploadId);
        return;
    }
    //beforeUpload
    if (!options.beforeUpload.apply(null, [file])) {
        cleanupTask(uploadId);
        return;
    }

    // 分配角色
    var mediaType = options.signature.media_type;
    getUploadObj({
        id: options.signature.id,
        id_prefix: options.signature.id_prefix || 'teacher',
        media_type: mediaType,
        ext: ext,
    })
        .then(function (data) {
            // const data = {
            //     "path": "videoweike/teacher/weike/20ebe/1716173894.2737.56584.202999282.mp4",
            //     "resource_id": 202999282,
            //     "bucket": "umu.co",
            //     "region": "ap-northeast-1",
            //     "timestamp": 1716173894,
            //     "prefix": "",
            //     "sp": 2
            // }
            // CO 站点域名替换成对应的[cdn-alias].s3.[region].amazonaws.com/[path]域名
            // const data = {
            //     "path": "videoweike/teacher/weike/20ebe/1716173894.2737.56584.202999282.mp4",
            //     "resource_id": 202999282,
            //     "bucket": "umu.co",
            //     "region": "ap-northeast-1",
            //     "timestamp": 1716173894,
            //     "prefix": "",
            //     "sp": 2,
            //      // CO 站点域名替换成对应的[cdn-alias].s3.[region].amazonaws.com/[path]域名
            //     "aws_url": "https://umu-jp-f7hbh6ryp6ekj76yujucf6thrp5u6apn1a-s3alias.s3.ap-northeast-1.amazonaws.com/videoweike/teacher/weike/20ebe/1716173894.2737.56584.202999282.mp4",
            //     "enable_new_bucket": 1,
            // }

            if (!data) {
                logger.errorAndLog('[umu-upload aws] getUploadObj data is empty');
                cleanupTask(uploadId);
                options && options.error && options.error();
                return;
            }

            initSDK(data.region)
                .then(function () {
                    var resourceId = data.resource_id;
                    var success = function (source) {
                        var sourcePath = source.Location;
                        sourcePath = replaceAWSPath(sourcePath);

                        var resourceInfo = {
                            resource_id: resourceId,
                            path: sourcePath,
                        };

                        if (!options.customCallback) {
                            if (!hasResourceCallback(mediaType)) {
                                cleanupTask(uploadId);
                                options.success && options.success(null, resourceInfo);
                                return;
                            }

                            // 最开始是并行的，后面是串行的，主要原因是：有上传 ppt 转换的逻辑时，需要对 resource 做权限校验，如果并行上传，会导致获取 resource 数据延迟，导致权限校验失败
                            if (options.syncUploadCallback) {
                                // 同步上传回调，等待回调成功后再执行成功回调
                                // 如果回调失败，也执行成功回调
                                uploadCallback({
                                    media_type: mediaType,
                                    resource_id: resourceId,
                                    file_name: file.name,
                                    file_size: file.size,
                                    path: sourcePath,
                                    ext: ext,
                                    extend_info: options.extend_info || '',
                                })
                                    .then(function () {
                                        cleanupTask(uploadId);
                                        options.success && options.success(null, resourceInfo);
                                    })
                                    .catch(function (e) {
                                        logger.errorAndLog('[umu-upload aws]: uploadCallback error:', e);
                                        // 如果有错误，也执行成功回调，只是 resourceInfo.uploadCallbackError 会有值
                                        resourceInfo.uploadCallbackError = e;
                                        cleanupTask(uploadId);
                                        options.success && options.success(null, resourceInfo);
                                    });
                            } else {
                                uploadCallback({
                                    media_type: mediaType,
                                    resource_id: resourceId,
                                    file_name: file.name,
                                    file_size: file.size,
                                    path: sourcePath,
                                    ext: ext,
                                    extend_info: options.extend_info || '',
                                });
                                cleanupTask(uploadId);
                                options.success && options.success(null, resourceInfo);
                            }
                        } else {
                            if (isFunction(options.customCallback)) {
                                options.customCallback.apply(null, [resourceInfo]);
                            }
                            cleanupTask(uploadId);
                        }
                    };

                    var error = function (e) {
                        logger.errorAndLog('[umu-upload aws]: awsUpload error', e);
                        cleanupTask(uploadId);
                        options.error && options.error();
                    };

                    // 将isCustomEndpoint挂载到window上，用于判断是否是自定义的endpoint, 方便在aws的sdk中使用
                    window.isCustomEndpoint = parseInt(data.enable_new_bucket, 10) === 1;
                    var customEndpoint = window.isCustomEndpoint
                        ? tool.getURLOriginFromURLString(data.aws_url)
                        : undefined;

                    awsUpload(
                        {
                            file: file,
                            key: data.path,
                            resourceId: data.resource_id,
                            bucket: data.bucket,
                            progress: options.progress,
                            region: data.region,
                            uploadId: uploadId,
                        },
                        customEndpoint
                    ).then(success, error);
                })
                .catch(function (e) {
                    logger.errorAndLog('[umu-upload aws] initSDK error', e);
                    cleanupTask(uploadId);
                    options.error && options.error();
                });
        })
        .catch(function (e) {
            logger.errorAndLog('[umu-upload aws] getUploadObj error', e);
            cleanupTask(uploadId);
            options.error && options.error();
        });

    // 返回上传ID，用于取消上传
    return uploadId;
}

/**
 * 获取当前所有上传任务的状态
 * @returns {Array} 当前上传任务列表
 */
function getUploadTasks() {
    var tasks = [];
    for (var uploadId in currentUploadTasks) {
        if (currentUploadTasks.hasOwnProperty(uploadId)) {
            var task = currentUploadTasks[uploadId];
            tasks.push({
                uploadId: uploadId,
                fileName: task.file ? task.file.name : '',
                fileSize: task.file ? task.file.size : 0,
                startTime: task.startTime,
            });
        }
    }
    return tasks;
}

upload.cancelUpload = cancelUpload;
upload.getUploadTasks = getUploadTasks;

module.exports = upload;
